import api from './api';

// Thin wrappers around the PipelineHealthController endpoints
// (see api/EllipsisATICotis/Controllers/PipelineHealthController.cs,
// swagger at https://localhost:7155/swagger/index.html).
//
// Each function returns the raw axios promise so callers can decide
// how to surface loading / error state. String filters are omitted
// from the URL when empty so the C# defaults kick in.

// daysWindow values surfaced by the dashboard dropdowns.
export const DAYS_WINDOW_OPTIONS = [1, 7, 30, 90];

function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (typeof v === 'string' && v.length === 0) return;
    search.append(k, String(v));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * GET /api/pipelinehealth/overview
 * @param {number} daysWindow Look-back window in days (1, 7, 30, 90). Default 7.
 * @param {string} sourceFilter Optional pipeline source (from /sources).
 */
export function getPipelineHealthOverview(daysWindow = 7, sourceFilter = '') {
  return api.get(`pipelinehealth/overview${buildQuery({ daysWindow, sourceFilter })}`);
}

/**
 * GET /api/pipelinehealth/domains
 * Returns the distinct domain values present in the fact table.
 */
export function getPipelineHealthDomains(hasSla) {
  const params = hasSla ? `?hasSla=${hasSla}` : ''
  return api.get(`pipelinehealth/domains${params}`);
}

/**
 * GET /api/pipelinehealth/sources
 * Returns the distinct pipeline sources, each with its list of domains.
 */
export function getPipelineHealthSources() {
  return api.get('pipelinehealth/sources');
}

/**
 * GET /api/pipelinehealth/entityTypes
 * Returns the entity types present in the fact table, each with the list
 * of entity names that fall under it. Optionally narrowed to a single
 * domain. Shape: [{ type: string, entities: string[] }].
 * @param {string} domain Optional domain to restrict the entity-type list.
 */
export function getPipelineHealthEntityTypes(domain = '') {
  return api.get(`pipelinehealth/entityTypes${buildQuery({ domain })}`);
}

/**
 * GET /api/pipelinehealth/domains/{domain}
 * @param {string} domain Required domain key.
 * @param {number} daysWindow Look-back window in days (1, 7, 30, 90). Default 30.
 * @param {string} sourceFilter Optional pipeline source (from /sources).
 */
export function getPipelineHealthDomainDeepDive(domain, daysWindow = 30, sourceFilter = '') {
  return api.get(
    `pipelinehealth/domains/${encodeURIComponent(domain)}${buildQuery({ daysWindow, sourceFilter })}`
  );
}

/**
 * GET /api/pipelinehealth/volume
 * @param {number} daysWindow Look-back window in days (1, 7, 30, 90). Default 7.
 * @param {number} topZScores Max entries in the top-z list (≤ 50). Default 10.
 * @param {string} sourceFilter Optional pipeline source (from /sources).
 * @param {string} domain Optional domain to further narrow the scatter.
 */
export function getPipelineHealthVolume(daysWindow = 7, topZScores = 10, sourceFilter = '', domain = '') {
  return api.get(
    `pipelinehealth/volume${buildQuery({ daysWindow, topZScores, sourceFilter, domain })}`
  );
}

/**
 * GET /api/pipelinehealth/sla
 * @param {number} daysWindow Look-back window in days (1, 7, 30, 90). Default 7.
 * @param {string} domain Optional domain to restrict the SLA roll-up.
 */
export function getPipelineHealthSla(daysWindow = 7, domain = '') {
  return api.get(`pipelinehealth/sla${buildQuery({ daysWindow, domain })}`);
}

/**
 * GET /api/pipelinehealth/errors
 * @param {number} daysWindow Look-back window in days (1, 7, 30, 90). Default 7.
 * @param {string} domain Domain for the incident timeline. Default "hms" (set server-side).
 * @param {number} topMessages Max error messages (≤ 50). Default 10.
 * @param {string} entityFilter Optional entity (from /entities) to scope every section to a single entity.
 */
export function getPipelineHealthErrors(daysWindow = 7, domain = '', topMessages = 10, entityFilter = '') {
  return api.get(
    `pipelinehealth/errors${buildQuery({ daysWindow, domain, topMessages, entityFilter })}`
  );
}

/**
 * GET /api/pipelinehealth/snapshots
 * @param {string} domain Snapshot-based domain.
 * @param {number} limit Max inventory rows (≤ 5000). Default 500.
 */
export function getPipelineHealthSnapshots(domain, limit = 500) {
  return api.get(`pipelinehealth/snapshots${buildQuery({ domain, limit })}`);
}

/**
 * POST /api/pipelinehealth/cache/invalidate
 * Forces every dashboard cache entry to be rebuilt on the next request.
 * Caller must have Admin / CustomerSupport / GlobalAdmin / PipelineHealth.
 */
export function invalidatePipelineHealthCache() {
  return api.post('pipelinehealth/cache/invalidate');
}
